import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Shield, FileJson, CheckCircle2, AlertCircle, Loader2, Users, Database, Sparkles, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../api/client';

const SAMPLE = `[
  {
    "name": "Beispiel Lady",
    "category": "Sie sucht ihn",
    "region": "Zürich (Stadt)",
    "description": "Diskrete Begleitung, niveauvoll und gepflegt.",
    "offers": 5,
    "price": "ab CHF 200",
    "top": false
  }
]`;

const AdminPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [jsonText, setJsonText] = useState('');
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [sampleCount, setSampleCount] = useState(5);
  const [sampleBusy, setSampleBusy] = useState(false);
  const [sampleResult, setSampleResult] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.is_admin) navigate('/inserate');
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      adminApi.stats().then(setStats).catch(() => {});
    }
  }, [user, result, sampleResult]);

  const generateSamples = async () => {
    setSampleBusy(true); setSampleResult(null);
    try {
      const res = await adminApi.generateSamples(sampleCount);
      setSampleResult(res);
    } catch (err) {
      setSampleResult({ error: err?.response?.data?.detail || 'Fehler beim Generieren' });
    } finally { setSampleBusy(false); }
  };

  const deleteSamples = async () => {
    if (!window.confirm('Wirklich ALLE Sample-Inserate löschen?')) return;
    setSampleBusy(true);
    try {
      const res = await adminApi.deleteSamples();
      setSampleResult({ deleted: res.deleted });
    } catch (err) {
      setSampleResult({ error: err?.response?.data?.detail || 'Fehler' });
    } finally { setSampleBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setJsonText(text);
  };

  const submit = async () => {
    setError(''); setResult(null); setBusy(true);
    try {
      let parsed;
      try {
        parsed = JSON.parse(jsonText);
      } catch (e) {
        throw new Error('Ungültiges JSON');
      }
      const listings = Array.isArray(parsed) ? parsed : parsed.listings;
      if (!Array.isArray(listings)) throw new Error('JSON muss ein Array oder { listings: [...] } sein');
      const res = await adminApi.import(listings, replaceExisting);
      setResult(res);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Fehler beim Import');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user?.is_admin) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#9a8a83]"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0f0a0c]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#a83a5b] to-[#5e1c30] flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <h1 className="font-serif text-3xl text-white">Admin-Bereich</h1>
        </div>
        <p className="text-[#9a8a83] mb-8">Angemeldet als <span className="text-white">{user.email}</span></p>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-[#7a6b65] mb-1 inline-flex items-center gap-1.5"><Database className="w-3.5 h-3.5" /> Total</div>
              <div className="font-serif text-2xl text-white">{stats.total_listings}</div>
            </div>
            <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-[#7a6b65] mb-1">Matchdate</div>
              <div className="font-serif text-2xl text-white">{stats.by_source.dm || 0}</div>
            </div>
            <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-[#7a6b65] mb-1">xdate.ch</div>
              <div className="font-serif text-2xl text-white">{stats.by_source.xd || 0}</div>
            </div>
            <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-[#7a6b65] mb-1 inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Users</div>
              <div className="font-serif text-2xl text-white">{stats.users}</div>
            </div>
          </div>
        )}

        <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-6 mb-6">
          <h2 className="font-serif text-xl text-white mb-2 inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#c19a8a]" /> Beispiel-Inserate generieren
          </h2>
          <p className="text-sm text-[#9a8a83] mb-4">
            Erstelle in einem Schritt bis zu 10 realistische Demo-Inserate mit zufälligen Namen, Städten,
            Telefonnummern und Services. Perfekt zum schnellen Befüllen.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-[#241a1f] border border-[#2a1f24] rounded-md px-2 py-1.5">
              <label className="text-xs text-[#9a8a83]">Anzahl</label>
              <input type="number" min={1} max={10} value={sampleCount}
                onChange={(e) => setSampleCount(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                className="w-14 bg-transparent text-white text-sm focus:outline-none text-center" />
              <div className="flex flex-col -gap-1">
                <button onClick={() => setSampleCount((c) => Math.min(10, c + 1))}
                  className="text-[10px] text-[#9a8a83] hover:text-white leading-none">▲</button>
                <button onClick={() => setSampleCount((c) => Math.max(1, c - 1))}
                  className="text-[10px] text-[#9a8a83] hover:text-white leading-none">▼</button>
              </div>
            </div>
            <button onClick={generateSamples} disabled={sampleBusy}
              className="px-5 py-2.5 rounded-md bg-[#a83a5b] hover:bg-[#bf456b] disabled:opacity-60 text-white font-medium inline-flex items-center gap-2">
              {sampleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {sampleCount} Beispiel{sampleCount > 1 ? 'e' : ''} einfügen
            </button>
            <div className="flex items-center gap-1 flex-wrap">
              {[1, 3, 5, 10].map((n) => (
                <button key={n} onClick={() => setSampleCount(n)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    sampleCount === n
                      ? 'bg-[#241a1f] border-[#a83a5b] text-white'
                      : 'bg-transparent border-[#2a1f24] text-[#9a8a83] hover:text-white'
                  }`}>
                  {n}
                </button>
              ))}
            </div>
            <button onClick={deleteSamples} disabled={sampleBusy || (stats?.by_source?.sample || 0) === 0}
              className="ml-auto px-3 py-2 rounded-md border border-[#2a1f24] text-[#ff8aa3] hover:text-white hover:bg-[#5c1a1a] hover:border-[#5c1a1a] text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> Alle Beispiele löschen
            </button>
          </div>
          {sampleResult && (
            <div className={`mt-4 p-3 rounded-md border text-sm ${
              sampleResult.error
                ? 'bg-[#3a1018] border-[#7a2030] text-[#ffb3c1]'
                : 'bg-[#1c2a1f] border-[#2f5a35] text-[#9af0a8]'
            }`}>
              {sampleResult.error ? (
                <span className="inline-flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {sampleResult.error}</span>
              ) : sampleResult.deleted !== undefined ? (
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {sampleResult.deleted} Beispiele gelöscht</span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> {sampleResult.created} Beispiel-Inserate erstellt
                </span>
              )}
            </div>
          )}
        </div>

        <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-6 mb-6">
          <h2 className="font-serif text-xl text-white mb-4 inline-flex items-center gap-2">
            <FileJson className="w-5 h-5 text-[#c19a8a]" /> Inserate als JSON importieren
          </h2>
          <p className="text-sm text-[#9a8a83] mb-4">
            Lade eine JSON-Datei hoch oder füge JSON direkt ein. Format: Array von Objekten mit den Feldern{' '}
            <code className="text-[#c19a8a]">name, category, region, description, offers, price, top</code>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <label className="flex items-center justify-center gap-2 px-4 py-3 rounded-md border border-dashed border-[#3a2530] hover:border-[#a83a5b] cursor-pointer text-[#b8a9a3]">
              <Upload className="w-4 h-4" />
              <span>Datei wählen (.json)</span>
              <input type="file" accept="application/json,.json" onChange={onFile} className="hidden" />
            </label>
            <label className="flex items-center gap-2 px-4 py-3 rounded-md bg-[#241a1f] border border-[#2a1f24] text-[#b8a9a3] cursor-pointer">
              <input type="checkbox" checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)}
                className="accent-[#a83a5b]" />
              <span className="text-sm">Vorherige Importe ersetzen</span>
            </label>
          </div>

          <textarea rows={12} placeholder={SAMPLE} value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            className="w-full bg-[#241a1f] border border-[#2a1f24] rounded-md p-3 text-xs font-mono text-white placeholder:text-[#5a4d48] focus:outline-none focus:border-[#a83a5b]" />

          <div className="flex items-center justify-between mt-4">
            <button onClick={() => setJsonText(SAMPLE)} className="text-xs text-[#c19a8a] hover:text-white">
              Beispiel einfügen
            </button>
            <button onClick={submit} disabled={busy || !jsonText.trim()}
              className="px-5 py-2.5 rounded-md bg-[#a83a5b] hover:bg-[#bf456b] disabled:opacity-60 text-white font-medium inline-flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {busy ? 'Importiere...' : 'Importieren'}
            </button>
          </div>

          {error && (
            <div className="mt-4 p-3 rounded-md bg-[#3a1018] border border-[#7a2030] text-sm text-[#ffb3c1] inline-flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}

          {result && (
            <div className="mt-4 p-4 rounded-md bg-[#1c2a1f] border border-[#2f5a35] text-sm">
              <div className="text-[#9af0a8] inline-flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4" /> Import abgeschlossen
              </div>
              <p className="text-[#b8a9a3]">
                <span className="text-white">{result.created}</span> erstellt,{' '}
                <span className="text-white">{result.skipped}</span> übersprungen
              </p>
              {result.errors && result.errors.length > 0 && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-[#c19a8a] text-xs">Fehler anzeigen ({result.errors.length})</summary>
                  <ul className="mt-2 space-y-1 text-xs text-[#9a8a83]">
                    {result.errors.map((e, i) => (
                      <li key={i}>#{e.index} {e.name}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="bg-[#1a1417] border border-[#2a1f24] rounded-xl p-6">
          <h3 className="font-serif text-lg text-white mb-3">Erlaubte Werte</h3>
          <p className="text-xs text-[#7a6b65] mb-2">
            <span className="text-[#c19a8a]">category</span>: Sie sucht ihn, Er sucht sie, Sex-Party / Swinger, Transsexuelle,
            Domina / BDSM, Escort Service, Massagen, MILF & reife Frauen, Gay, Webcam, Bilder & Videos, Clubs,
            Stellen & Jobs, Immobilien
          </p>
          <p className="text-xs text-[#7a6b65]">
            <span className="text-[#c19a8a]">region</span>: Aargau, Appenzell, Bern, Basel Stadt, Basel Land, Fribourg,
            Genf, Glarus, Graubünden, Jura, Luzern, Neuenburg, Nidwalden &amp; Obwalden, Solothurn, St. Gallen,
            Schaffhausen, Schwyz, Thurgau, Tessin, Uri, Waadt, Wallis, Zug, Zürich (Stadt), Zürich (Region), Online,
            Deutschland, Österreich, Liechtenstein
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
